import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { AddressService } from 'core/address/address.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SearchService } from 'core/search/search.service';
import { CanComponentDeactivate } from 'core/unsaved-changes/unsaved-changes-guard.class';
import {
  countriesWithHiddenSTateOrProvinceField,
  countriesWithoutProvinceOrStateInput, countriesWithPostalCodeField, countriesWithProvince, countryCodeValues,
  regionDropDownCountries
} from 'core/app-info/country-specifics.enum';
import { UtilsService } from 'core/utils/utils.service';
import { LeadData } from 'src/app/modules/shared/entities/lead-data.class';
import { valueInListValidator } from 'src/app/modules/shared/value-in-list-validator/value-in-list-validator';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountManager } from '../../../../../app/modules/shared/entities/account-manager.class';
import { QuoteDetailsService } from '../../../pos/quotes/services/quote-details.service';
import { Company } from '../../../shared/entities/company.class';
import { AccountsDetailService } from '../../accounts/services/accounts-detail.service';
import { AccountsListService } from '../../accounts/services/accounts-list.service';
import { ContactsDashboardComponent } from '../../contacts/contacts-dashboard/contacts-dashboard.component';
import { LeadsDeleteComponent } from '../delete-lead/leads-delete.component';
import { LeadsService } from '../leads.service';
import { AccountDetailsByLead } from '../entities/account-details-byLead.class';
import { DeliveryLocationCompanyCountry } from '../entities/delivery-location-company-country.class';
import { County } from '../entities/county.class';

@Component({
  selector: 'app-edit-lead',
  templateUrl: './edit-lead.component.html',
  styleUrls: ['./edit-lead.component.scss'],
  standalone: false
})
export class EditLeadComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @Input() action: UrlSegment;
  @Input() deliveryIdInput;
  @Input() leadIdInput;

  @ViewChild(ContactsDashboardComponent) contactDashboard: ContactsDashboardComponent;

  title: string;
  appStrings: AppLocalization;
  leadType: string;
  isNewLead: boolean;
  isNewDelivery: boolean;
  isDashboardRedirect: boolean;
  isDeliveryEditMode = false;
  isLeadEditMode = false;
  customerId: string;
  deliveryId: string;
  leadId: string;
  countryWithCounty: boolean;
  countyListPerRegion = [];
  addressFormControls: any;

  companiesList: { value: any, label: string }[] = [];
  companyDetails: Company;
  disableEditDelivery = false;
  isLoading = false;
  isLoadingData = true;
  isOnline: boolean;
  countyList: any = {};
  regionList: any = {};

  formFields = {
    company: [null, Validators.required],
    accountManager: [null, Validators.required],
    sourceTypeId: [null],
    address: this.fb.group({
      id: [this.appInfoService.emptyGuid],
      addressline1: ['', [Validators.maxLength(30), Validators.required]],
      addressline2: ['', Validators.maxLength(30)],
      city: ['', [Validators.maxLength(30), Validators.required]],
      zip: ['', [Validators.maxLength(10), Validators.required]],
      countryId: [null, Validators.required],
      countyId: null,
      regionId: null,
      stateId: [null, Validators.required],
      province: this.fb.group({
        name: ['', Validators.required],
        id: [this.appInfoService.emptyGuid, Validators.required]
      })
    }),
  };
  maxLengthsInputs = {
    company: 50,
  };
  countries;
  templateValues = {
    country: {},
    states: []
  };
  templateValuesCountry: any;
  sources: { value: any, label: string }[] = [];
  leadForm = this.fb.group(this.formFields);
  zipControl: AbstractControl;
  isAdminTypeUser: boolean;
  accountManagerList: AccountManager[] = [];
  isMobile: boolean;
  displayAccountManagerList: { value: string, label: string }[] = [];
  newDeliveryLocation = 'new-delivery-location';
  leadsManagement = 'leads-management';
  leadTypes = {};
  lnStatuses = {};
  addressFormGroup: any;
  detailsByLead: AccountDetailsByLead;
  canEdit: boolean;
  leadIdToUse: string;
  contactsZipOrPostalCode: string;
  countryWithoutStateField: boolean;
  countryWithHiddenStateProvince: boolean;
  leadStatusCodeList: any[] = [];
  lnStatusCodeList: any[] = [];

  displayCountryList: { value: any, label: string }[] = [];
  displayCountyList: { value: any, label: string }[] = [];
  displaySourcesList: { value: any, label: string }[] = [];
  displayStatesList: { value: any, label: string }[] = [];
  displayRegionList: { value: any, label: string }[] = [];

  private hotKeyListener = ($event: KeyboardEvent) => this.checkHotKeys($event);
  constructor(
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private addressService: AddressService,
    private notificationService: NotificationService,
    private leadsService: LeadsService,
    private router: Router,
    private searchService: SearchService,
    private accountsListService: AccountsListService,
    private accountDetailsService: AccountsDetailService,
    private quoteDetailsService: QuoteDetailsService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit() {
    this.isOnline = this.appStateService.state.isOnline;
    this.isDashboardRedirect = this.route.snapshot.url[1]?.path === this.leadsManagement;
    this.appStrings = this.appInfoService.appStrings;
    this.zipControl = (this.leadForm.controls.address as FormGroup).controls.zip;
    this.addressFormControls = (this.leadForm.controls.address as FormGroup).controls;
    this.isMobile = this.deviceService.isMobile();

    if (typeof this.deliveryIdInput !== 'undefined') {
      this.deliveryId = this.deliveryIdInput.path || null;
    }
    if (typeof this.leadIdInput !== 'undefined' && this.leadIdInput) {
      this.leadId = this.leadIdInput.path || null;
    }

    this.isDeliveryEditMode = !!this.deliveryId;
    this.isLeadEditMode = !!this.leadId;

    this.setLeadType();
    this.setPageTitle();
    this.determineZipOrPostalCode();

    this.isNewLead = this.leadType === this.appStrings.label_lead && !this.isLeadEditMode;
    this.isNewDelivery = this.leadType === this.appStrings.label_delivery && !this.isDeliveryEditMode;

    if (this.isNewLead || this.isNewDelivery) {
      this.canEdit = true;
    }

    this.isAdminTypeUser = this.appInfoService.isAdminTypeUser();
    this.processTemplateValues();

    const addressCtrl = this.leadForm.controls.address as FormGroup;
    (addressCtrl.controls.province as FormGroup).controls.name.markAsTouched();

    if (this.isNewLead || this.isDeliveryEditMode || this.isLeadEditMode) {
      this.leadForm.controls.company.enable();
    }

    if (this.leadId) {
      this.leadIdToUse = this.leadId;
    } else {
      this.leadIdToUse = this.deliveryId;
    }

    if (this.leadIdToUse) {
      this.accountDetailsService.getAccountDetailsByLead(this.leadIdToUse).subscribe(
        result => {
          this.detailsByLead = result;
          this.canEdit = this.detailsByLead.canEdit;
          this.toggleDisabledFields();
        }
      );
    }
    window.document.addEventListener('keydown', this.hotKeyListener);
    this.addressFormGroup = this.leadForm.controls.address as FormGroup;

    if (this.isNewLead || this.isLeadEditMode) {
      this.applyLeadValidation();
    }
  }

  ngOnDestroy() {
    window.document.removeEventListener('keydown', this.hotKeyListener);
  }

  /**
   * Sets title of the page and lead type
   */
  private setLeadType() {
    if (this.action.path === this.newDeliveryLocation) {
      this.leadType = this.appStrings.label_delivery;
    } else {
      if (this.isDeliveryEditMode) {
        this.leadType = this.appStrings.label_delivery;
      } else {
        this.leadType = this.appStrings.label_lead;
      }
    }
  }

  /**
   * Sets the page title based on the current action and mode
   */
  private setPageTitle() {
    if (this.action.path === this.newDeliveryLocation) {
      this.title = this.appStrings.lead_newDeliveryMessage;
    } else if (this.isDeliveryEditMode) {
      this.title = this.appStrings.lead_editDelivery_title;
    } else if (this.isLeadEditMode) {
      this.title = this.appStrings.label_edit_lead;
    } else {
      this.title = this.appStrings.lead_newLead_title;
    }
  }

  /**
   * Gets and process template values
   */
  private processTemplateValues() {
    this.addressService.getTemplateValues(([countryValues, stateValues, regionValues, countyValues]) => {
      this.templateValuesCountry = countryValues.Countries;
      this.displayCountryList = this.utilsService.mapObjectToSelect(this.templateValuesCountry, 'Key', 'Value');
      this.templateValues.states = this.addressService.getStateListPerCountry(stateValues);
      this.regionList = this.addressService.getRegionListPerCountry(regionValues, this.templateValuesCountry);
      this.countyList = this.addressService.getCountyListPerRegion(countyValues);
      this.displayCountyList = this.utilsService.mapObjectToSelect(countyValues, 'id', 'countyName');

      this.getAccountManagerList(() => {
        if (this.isDeliveryEditMode) {
          this.leadsService.getDeliveryLocationDetails(this.deliveryId, (result: LeadData) => {
            this.setValues(result);
            this.isLoadingData = false;
          });
        } else if (this.isLeadEditMode) {
          this.leadsService.getLeadDetails(this.leadId, (result: LeadData) => {
            this.setValues(result);
            this.isLoadingData = false;
          });
        } else {
          this.selectDefaultCountry();
          this.isLoadingData = false;
        }
      });

      this.accountsListService.getTemplateValues(templateValues => {
        this.leadTypes = templateValues.LeadType;
        this.lnStatuses = templateValues.LnStatus;
        this.sources = this.utilsService.mapNumberKeyValueOptions(templateValues.SourceType);
        this.displaySourcesList = this.sources;
      });
    });

  }

  private applyLeadValidation() {
    this.leadForm.controls.sourceTypeId.addValidators([Validators.required]);
    this.addressFormGroup.controls.countryId.clearValidators();
    this.addressFormGroup.controls.zip.removeValidators([Validators.required]);
    this.addressFormGroup.controls.addressline1.removeValidators([Validators.required]);
  }

  /**
   * Display function for Account Manager
   * @param accountManager - The account manager object
   */
  displayAccountManager(accountManager: AccountManager) {
    return accountManager?.name || '';
  }

  /**
   * Account Manager selection changed
   * @param event - The event
   */
  selectedAccountManager(event: MatAutocompleteSelectedEvent) {
    this.setAccountManager(event.option.value);
  }

  /**
   * Sets selected account manager
   * @param accountManager - selected account manager
   */
  private setAccountManager(id: string) {
    const accountManager = this.accountManagerList.find(am => am.id === id);
    if (!accountManager) {
      return;
    }

    if (this.isLeadEditMode || this.isDeliveryEditMode || this.isNewDelivery) {
      this.leadForm.controls.company.reset();
    }
    if (!this.isNewLead && !this.isLeadEditMode) {
      this.getCompanies(accountManager.employeeId);
    }
  }

  /**
   * Gets companies list by account manager id
   * @param amId - selected account manager id
   * @param onComplete - optional callback to execute after companies are loaded
   */
  private getCompanies(amId: string, onComplete?: () => void) {
    this.quoteDetailsService.getCustomers(amId, companies => {
      this.leadForm.controls.company.enable();
      this.companiesList = this.utilsService.mapCustomersToSelect(companies, this.leadTypes, this.lnStatuses);

      setTimeout(() => {
        this.leadForm.controls.company.updateValueAndValidity();
        this.cdr.detectChanges();
        if (onComplete) {
          onComplete();
        }
      }, 0);
    });
  }

  /**
   * Sets form values
   * @param values - form data
   */
  private setValues(values: Partial<LeadData>) {
    values.address.addressline1 = values.address.addressline1 || '';
    values.address.addressline2 = values.address.addressline2 || '';
    values.address.city = values.address.city || '';
    values.address.province = values.address.province || {
      id: this.appInfoService.emptyGuid,
      name: ''
    };
    values.address.zip = values.address.zip || '';

    this.renderAccountManager(values);
    this.leadForm.controls.address.setValue(values.address);
    this.leadForm.controls.accountManager.setValue(values.accountManager?.id);
    this.leadForm.controls.sourceTypeId.setValue(values.sourceTypeId);
    this.updateCountryValue(values.address.countryId);
    this.countryWithCounty = regionDropDownCountries.includes(
      this.addressService.getCountryValueById(values.address.countryId));
    this.updateCountyListPerRegion(values.address.regionId);
    this.updateCountyValue(values.address.countyId);

    let companyId: string;
    let companyLabel: string;

    if (this.isLeadEditMode) {
      values.company.name = values.company.name || '';
      values.company.id = values.company.id || this.appInfoService.emptyGuid;
      this.companyDetails = values.company;
      companyId = values.company.id;
      companyLabel = values.company.name;
    } else {
      values.customer.bpNumber = values.customer.bpNumber || this.appInfoService.emptyGuid;
      values.customer.name = (values.customer.name1 || '') + (' ' + values.customer.name2 || '');
      values.customer.status = values.customer.bpStatus || '';
      companyId = values.customer.id;
      companyLabel = values.customer.name1;
    }

    this.renderCompany(companyId, companyLabel);
    this.getAccountManagerList();

    if (!this.isLeadEditMode) {
      this.loadCompaniesForLead(values);
    }
    this.disableEditDelivery = values.leadStatus !== 1;
    this.toggleDisabledFields();
  }

  /**
   * Toggle for disabled status
   */
  toggleDisabledFields() {
    if (this.disableEditDelivery || !this.canEdit) {
      this.leadForm.disable();
    } else {
      this.leadForm.enable();
    }
  }

  /**
   * Gets account managers list
   */
  getAccountManagerList(onComplete?: () => void) {
    const localCallback = (accountManagers: AccountManager[]) => {
      this.accountManagerList = accountManagers;
      this.displayAccountManagerList = accountManagers.map(am => ({
        value: am.id,
        label: am.terminated ? 'TERM - ' + am.name : am.name
      }));
      this.applyAccountManagerListValidator();
      if (onComplete) {
        onComplete();
      }
    };
    this.leadsService.getAccountManagerList(localCallback);
  }

  private applyAccountManagerListValidator(): void {
    const accountManagerCtrl = this.leadForm.controls.accountManager;
    const validValues = this.displayAccountManagerList.map(am => am.value);

    accountManagerCtrl.setValidators([Validators.required, valueInListValidator(validValues)]);
    accountManagerCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Renders the selected company in the companies list and sets the form control value
   * @param companyId - The ID of the company to render
   * @param companyLabel - The label/name of the company to display
   */
  private renderCompany(companyId: string, companyLabel: string): void {
    this.companiesList = [{
      value: companyId,
      label: companyLabel
    }];

    setTimeout(() => {
      this.leadForm.controls.company.setValue(companyId);
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * Renders the selected account manager in the account manager display list
   * @param values - The lead data containing the account manager information
   */
  private renderAccountManager(values: Partial<LeadData>): void {
    if (values.accountManager) {
      this.displayAccountManagerList = [{
        value: values.accountManager.id,
        label: values.accountManager.name
      }];
      this.accountManagerList = [values.accountManager];
    }
  }

  /**
   * Loads the companies list for a lead based on the account manager
   * @param values - The lead data containing the account manager information
   */
  private loadCompaniesForLead(values: Partial<LeadData>): void {
    const accountManager = this.accountManagerList.find(am => am.id === values.accountManager.id);
    if (accountManager) {
      this.getCompanies(accountManager.employeeId);
    }
  }

  /**
   * Checks if the form is valid
   */
  formsAreValid() {
    const addressLeadFormControls = this.leadForm.controls.address;

    const useStateDropdown = this.addressService.countryUsesStateDropdown(this.leadForm.value.address.countryId);
    const useProvinceInput = this.addressService.countryUsesProvinceFreeInput(this.leadForm.value.address.countryId);
    if (!useStateDropdown) {
      (addressLeadFormControls as FormGroup).controls.stateId.setErrors(null);
    }
    if (!useProvinceInput) {
      const provinceCtrl = (addressLeadFormControls as FormGroup).controls.province as FormGroup;
      provinceCtrl.controls.name.setErrors(null);
    }

    let valid = this.leadForm.valid;
    let formField;

    // no contact form in edit mode
    if ((!this.isDeliveryEditMode) && this.leadType !== this.appStrings.label_delivery && !this.isLeadEditMode) {
      // check the contact form as well
      valid = this.contactDashboard.validateFormIfNotEmpty() && valid;
    }
    this.leadForm.markAllAsTouched();

    Object.keys((addressLeadFormControls as FormGroup).controls).forEach(controlKey => {
      formField = addressLeadFormControls.get(controlKey);
      formField.markAsTouched();
      if (formField.hasOwnProperty('controls')) {
        Object.keys((formField as FormGroup).controls).forEach(ctrlKey => {
          const ctrlName = formField.get(ctrlKey);
          ctrlName.markAsTouched();
        });
      }
    });
    valid = valid && this.leadForm.valid; // re-check if the form is valid after touching controls
    return valid;
  }

  /**
   * Saves form changes in edit mode and save new lead/delivery location in create mode
   */
  saveChanges() {
    if (this.isLoading) {
      return;
    }

    let leadTypeIndex = Number(Object.keys(this.leadTypes).find(key => this.leadTypes[key] === this.leadType));

    if (!this.formsAreValid()) {
      this.notificationService.showNotification(this.appStrings.contacts_requiredFields, NotificationType.Error);
      return;
    }
    this.isLoading = true;

    if (this.isLeadEditMode) {
      leadTypeIndex = Number(Object.keys(this.leadTypes).find(key => this.leadTypes[key] === this.appStrings.label_lead));

      const companyFieldValue = this.leadForm.controls.company.value;

      if (typeof companyFieldValue === 'string' && companyFieldValue !== this.companyDetails.id) {
        this.companyDetails.name = companyFieldValue;
        this.companyDetails.id = this.appInfoService.emptyGuid;
      }

      this.leadForm.controls.company.setValue(this.companyDetails);
      this.leadsService.updateLead(
        this.leadForm.value,
        this.leadId,
        leadTypeIndex,
        1,
        () => this.redirectAndDisplayConfirmationMessage(this.appStrings.leads_leadCreatedNotification)
      );
      return;
    }

    if (this.leadType !== this.appStrings.label_delivery) {

      let contactsDashboard = this.contactDashboard.form.value;

      // Check for deleted phone, is not actually deleted
      if (!contactsDashboard.firstName) {
        contactsDashboard = null;
      } else {
        contactsDashboard.phones = contactsDashboard.phones.filter(phone => {
          if (phone.phoneNumber) {
            return phone;
          }
        });
      }

      this.leadsService.createContact(
        this.leadForm.value,
        contactsDashboard,
        this.appInfoService.emptyGuid,
        leadTypeIndex,
        1,
        () => this.redirectAndDisplayConfirmationMessage(this.appStrings.leads_leadCreatedNotification),
        () => this.isLoading = false
      );
    } else {
      this.leadsService.createDeliveryLocation(
        this.leadForm.value,
        this.appInfoService.emptyGuid,
        leadTypeIndex,
        1,
        () => this.redirectAndDisplayConfirmationMessage(this.appStrings.leads_deliveryLocationCreatedNotification)
      );
    }
  }

  /**
   * Updates delivery location changes
   */
  editDeliverySaveChanges() {
    if (this.isLoading) {
      return;
    }

    const leadTypeIndex = Number(Object.keys(this.leadTypes).find(key => this.leadTypes[key] === this.leadType));

    if (!this.formsAreValid()) {
      this.notificationService.showNotification(this.appStrings.contacts_requiredFields, NotificationType.Error);
      return;
    }

    this.isLoading = true;

    this.leadsService.updateDeliveryLocation(
      this.leadForm.value,
      this.deliveryId,
      leadTypeIndex,
      1,
      () => this.redirectAndDisplayConfirmationMessage(this.appStrings.leads_deliveryLocationCreatedNotification)
    );
  }

  /**
   * Take actions on hot keys press
   * @param $event - the Keyboard Event
   */
  checkHotKeys($event: KeyboardEvent) {
    if (this.canEdit) {
      if ($event.key === 's' && $event.ctrlKey) {
        $event.preventDefault();
        if (this.isDeliveryEditMode) {
          this.editDeliverySaveChanges();
        } else {
          this.saveChanges();
        }
      }
    }
  }


  /**
   * Check for new delivery location to allow only companies from list
   */
  companyInputControl() {
    setTimeout(() => {
      const companyCtrl = this.leadForm.controls.company;
      if ((this.isNewDelivery || this.isDeliveryEditMode) && companyCtrl.value) {
        if (!companyCtrl.value.id) {
          companyCtrl.reset();
        }
      }
    }, 300);
  }

  /**
   * Opens delete lead pop-up
   * @param event - the click event
   */
  confirmDeleteLead(event: Event) {
    const leadType = this.leadType;
    const leadId = this.isLeadEditMode ? this.leadId : this.deliveryId;

    event.stopPropagation();

    const dialogRef = this.dialog.open(LeadsDeleteComponent, {
      width: this.deviceService.isMobile() ? '' : '370px',
      panelClass: this.deviceService.isMobile() ? ['ds', 'fullWidth'] : [],
      data: {
        leadId,
        leadType
      }
    });

    dialogRef.afterClosed().subscribe(deletionIsDone => {
      if (deletionIsDone) {
        this.leadForm.markAsPristine();
      }
    });
  }

  /**
   * Redirects and displays confirmation message
   * @param message - message to display
   */
  private redirectAndDisplayConfirmationMessage(message: string) {
    this.notificationService.showNotification(message, NotificationType.Success);
    this.leadForm.markAsPristine();
    setTimeout(() => {
      this.isLoading = false;
      if (this.isDashboardRedirect) {
        this.router.navigate(['/dashboard']);
      } else {
        this.router.navigate(['/accounts']);
      }
    }, 1000);
  }

  /**
   * Sets default country
   */
  private selectDefaultCountry() {
    const countryCode = this.appInfoService.userData.UserInfo.CountryCode;
    const countryId = this.addressService.getCountryIdByValue(countryCodeValues[countryCode]);
    (this.leadForm.controls.address as FormGroup).controls.countryId.setValue(countryId);
    this.countryChangedEvent(countryId);
  }

  /**
   * Triggers update country process
   * @param countryValue - country key value
   */
  private setStatesList(countryId: number) {
    const statesRaw = this.templateValues.states[countryId] || [];
    this.displayStatesList = this.utilsService.mapObjectToSelect(statesRaw, 'stateId', 'stateName');
  }

  countryChangedEvent(countryId: any) {
    if (!countryId) {
      return;
    }

    this.updateCountryValue(countryId);
    this.addressFormControls.countyId.reset();
    this.addressFormControls.regionId.reset();
  }

  handleCountryChanged($event: any) {
    this.countryChangedEvent($event.value);
  }

  /**
   * Triggers update county list per region
   * @param regionValue -region key value
   */
  regionChangedEvent(regionId: any) {
    this.updateCountyListPerRegion(regionId);
    this.addressFormControls.countyId.reset();
    this.addressFormControls.countyId.enable();
    this.addressFormControls.countyId.updateValueAndValidity();
  }

  handleRegionChanged($event: any) {
    this.regionChangedEvent($event.value);
  }


  /**
   * Updates country
   * @param countryId - country key value
   */
  private updateCountryValue(countryId: number) {
    this.addressService.countryChanged(countryId, this.leadForm);

    if (this.isNewDelivery || this.isDeliveryEditMode) {
      this.zipControl.setValidators([Validators.required, this.zipControl.validator]);
      this.zipControl.updateValueAndValidity();
    }

    this.countryWithCounty = this.addressService.countryUsesRegionStateDropdown(countryId);

    if (this.countryWithCounty) {
      const regions = this.regionList[countryId] || [];
      this.displayRegionList = this.utilsService.mapObjectToSelect(regions, 'regionId', 'regionName');
      this.displayCountyList = [];
    } else {
      this.setStatesList(countryId);
    }

    const countryValue = this.addressService.getCountryValueById(countryId);
    this.countryWithoutStateField = countriesWithoutProvinceOrStateInput.includes(countryValue);
    this.countryWithHiddenStateProvince = countriesWithHiddenSTateOrProvinceField.includes(countryValue);
  }

  /**
   * Updates country value for delivery location
   * @param deliveryCompanyCountry -  country info object
   */
  updateCountryValueForDeliveryLocation(deliveryCompanyCountry: DeliveryLocationCompanyCountry) {
    (this.leadForm.controls.address as FormGroup).controls.countryId.setValue(deliveryCompanyCountry.id);
    this.countryChangedEvent(deliveryCompanyCountry.id);
  }

  /**
   * Confirm company change
   * @param event - the mat autocomplete selected event
   */
  confirmCompanyChange(event: MatAutocompleteSelectedEvent) {
    if (event.option.value) {
      this.leadsService.getCompanyDetails(event.option.value.bpNumber, result => this.updateCountryValueForDeliveryLocation(result));
    }
  }

  /**
   * Updates county list
   * @param countryId - region key value
   */
  private updateCountyListPerRegion(regionId: number) {
    this.countyListPerRegion = this.countyList[regionId];
    this.displayCountyList = this.countyListPerRegion
      ? this.utilsService.mapObjectToSelect(this.countyListPerRegion, 'id', 'countyName')
      : [];
    this.cdr.detectChanges();
  }

  /**
   * Updates county name
   * @param countyId
   */
  private updateCountyValue(countyId: any) {
    if (!this.countyListPerRegion) {
      return;
    }

    const selectedCounty = this.countyListPerRegion.find(county => county.id === countyId);
    this.addressFormControls.countyId.setValue(selectedCounty);
  }

  /**
   * Checks selected county value
   * @param eventValue - the mat autocomplete selected event
   */
  checkSelectedCounty(eventValue: MatAutocompleteSelectedEvent) {
    if (eventValue?.option?.value) {
      this.addressFormControls.countyId.setErrors(null);
    }
  }

  /**
   * Sets county name
   * @param countyObject - county object
   * @returns - county name
   */
  displayCounty(countyObject: County) {
    return countyObject?.countyName || '';
  }

  canDeactivate() {
    return !this.leadForm.dirty;
  }

  /**
   * Sets zip or postal code label
   */
  determineZipOrPostalCode() {
    if (countriesWithProvince && countriesWithPostalCodeField[this.appInfoService.userData.UserInfo.CountryCode]) {
      this.contactsZipOrPostalCode = this.appStrings.contacts_postalCode;
    } else {
      this.contactsZipOrPostalCode = this.appStrings.contacts_zip;
    }
  }

}
